<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :closable="false"
    :draggable="false"
    class="media-viewer-dialog border-none!"
    :style="{
      width: '100dvw',
      height: '100dvh',
      maxHeight: 'none',
      margin: 0,
    }"
    content-class="media-viewer-content"
    @hide="resetTransform"
  >
    <div class="relative flex h-full min-h-0 flex-col bg-[var(--p-surface-900)] text-white">
      <div class="flex h-14 shrink-0 items-center justify-between gap-3 px-3">
        <div class="min-w-0">
          <p class="truncate text-sm font-semibold">
            {{ currentItem?.fileName || mediaTitle }}
          </p>
          <p v-if="items.length > 1" class="text-xs text-white/65">
            {{ currentIndex + 1 }} / {{ items.length }}
          </p>
        </div>

        <div class="flex items-center gap-1">
          <button
            type="button"
            class="media-viewer-action cursor-pointer"
            title="Descargar"
            aria-label="Descargar archivo multimedia"
            @click="downloadCurrentItem"
          >
            <Download :size="19" />
          </button>
          <button
            type="button"
            class="media-viewer-action cursor-pointer"
            title="Ir al mensaje"
            aria-label="Ir al mensaje en el chat"
            @click="goToCurrentMessage"
          >
            <MessageSquareText :size="19" />
          </button>
          <button
            v-if="currentItem?.type === 'image'"
            type="button"
            class="media-viewer-action cursor-pointer"
            title="Alejar"
            aria-label="Alejar"
            @click="zoomOut"
          >
            <ZoomOut :size="19" />
          </button>
          <button
            v-if="currentItem?.type === 'image'"
            type="button"
            class="media-viewer-action cursor-pointer"
            title="Acercar"
            aria-label="Acercar"
            @click="zoomIn"
          >
            <ZoomIn :size="19" />
          </button>
          <button
            v-if="currentItem?.type === 'image'"
            type="button"
            class="media-viewer-action cursor-pointer"
            title="Girar a la izquierda"
            aria-label="Girar imagen a la izquierda"
            @click="rotateLeft"
          >
            <RotateCcw :size="19" />
          </button>

          <button
            v-if="currentItem?.type === 'image'"
            type="button"
            class="media-viewer-action cursor-pointer"
            title="Girar a la derecha"
            aria-label="Girar imagen a la derecha"
            @click="rotateRight"
          >
            <RotateCw :size="19" />
          </button>
          <button
            v-if="currentItem?.type === 'image'"
            type="button"
            class="media-viewer-action cursor-pointer"
            title="Restablecer"
            aria-label="Restablecer imagen"
            @click="resetTransform"
          >
            <RefreshCcw :size="19" />
          </button>
          <button
            type="button"
            class="media-viewer-action cursor-pointer"
            title="Cerrar"
            aria-label="Cerrar visor"
            @click="close"
          >
            <X :size="20" />
          </button>
        </div>
      </div>

      <div
        class="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden"
        @wheel.prevent="handleWheel"
      >
        <button
          v-if="canNavigate"
          type="button"
          class="media-viewer-nav left-10 cursor-pointer"
          title="Anterior"
          aria-label="Archivo anterior"
          @click="showPrevious"
        >
          <ChevronLeft :size="30" />
        </button>

        <img
          v-if="currentItem?.type === 'image'"
          :key="currentItem.id"
          :src="currentItem.url"
          :alt="currentItem.fileName || 'Imagen'"
          class="max-h-full max-w-full select-none object-contain transition-transform duration-150"
          :style="mediaTransformStyle"
          draggable="false"
        />

        <video
          v-else-if="currentItem?.type === 'video'"
          :src="currentItem.url"
          controls
          autoplay
          class="max-h-full max-w-full rounded"
        >
          Tu navegador no soporta la reproduccion de video.
        </video>

        <button
          v-if="canNavigate"
          type="button"
          class="media-viewer-nav right-10 cursor-pointer"
          title="Siguiente"
          aria-label="Archivo siguiente"
          @click="showNext"
        >
          <ChevronRight :size="30" />
        </button>
      </div>

      <div
        v-if="currentItem?.caption"
        class="bg-trnsparent! flex min-h-10 max-h-[30dvh] shrink-0 items-start justify-center overflow-y-auto border-t border-white/10 px-4 py-2 text-sm text-white/85"
      >
        <div class="md:w-3/4 text-center">
          <p
            ref="captionElement"
            :class="[
              'whitespace-pre-wrap break-words leading-relaxed',
              { 'media-viewer-caption-collapsed': !isCaptionExpanded },
            ]"
          >
            {{ currentItem.caption }}
          </p>
          <button
            v-if="hasCaptionOverflow && !isCaptionExpanded"
            type="button"
            class="cursor-pointer font-bold text-white hover:underline"
            @click="isCaptionExpanded = true"
          >
            Leer más
          </button>
        </div>
      </div>
      <!-- <div v-else  class="shrink-0 border-t border-white/10 px-4 py-6 text-sm text-white/85" /> -->
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useEventListener } from '@vueuse/core'
import Dialog from 'primevue/dialog'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  MessageSquareText,
  RefreshCcw,
  RotateCcw,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-vue-next'
import type { ChatMediaItem } from '../../composables/useChatMediaViewer'

interface Props {
  visible: boolean
  items: ChatMediaItem[]
  initialItemId?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [visible: boolean]
}>()

const currentIndex = ref(0)
const zoom = ref(1)
const rotation = ref(0)
const isCaptionExpanded = ref(false)
const hasCaptionOverflow = ref(false)
const captionElement = ref<HTMLElement | null>(null)

const isVisible = computed({
  get: () => props.visible,
  set: (visible) => emit('update:visible', visible),
})

const currentItem = computed(() => props.items[currentIndex.value])
const canNavigate = computed(() => props.items.length > 1)
const mediaTitle = computed(() => (currentItem.value?.type === 'video' ? 'Video' : 'Imagen'))
const mediaTransformStyle = computed(() => ({
  transform: `rotate(${rotation.value}deg) scale(${zoom.value})`,
  transformOrigin: 'center center',
  cursor: zoom.value > 1 ? 'zoom-out' : 'zoom-in',
}))

const resetTransform = () => {
  zoom.value = 1
  rotation.value = 0
}

const measureCaptionOverflow = () => {
  const caption = captionElement.value

  hasCaptionOverflow.value = Boolean(caption && caption.scrollHeight > caption.clientHeight + 1)
}

const setCurrentIndexFromInitialItem = () => {
  const index = props.items.findIndex((item) => item.id === props.initialItemId)
  currentIndex.value = index >= 0 ? index : 0
}

const showNext = () => {
  if (!props.items.length) return
  currentIndex.value = (currentIndex.value + 1) % props.items.length
  resetTransform()
  isCaptionExpanded.value = false
}

const showPrevious = () => {
  if (!props.items.length) return
  currentIndex.value = (currentIndex.value - 1 + props.items.length) % props.items.length
  resetTransform()
  isCaptionExpanded.value = false
}

const zoomIn = () => {
  zoom.value = Math.min(zoom.value + 0.2, 4)
}

const zoomOut = () => {
  zoom.value = Math.max(zoom.value - 0.2, 0.5)
}

const getFileNameFromUrl = (url: string) => {
  try {
    const pathFileName = new URL(url).pathname.split('/').pop()
    return pathFileName ? decodeURIComponent(pathFileName) : null
  } catch {
    const pathFileName = url.split('?')[0]?.split('/').pop()
    return pathFileName ? decodeURIComponent(pathFileName) : null
  }
}

const getDownloadFileName = (item: ChatMediaItem) => {
  const fileName = item.fileName || getFileNameFromUrl(item.url)

  if (fileName) return fileName

  return `${item.type}-${item.id}.${item.type === 'video' ? 'mp4' : 'jpg'}`
}

const triggerDownload = (url: string, fileName: string) => {
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

const downloadCurrentItem = async () => {
  const item = currentItem.value

  if (!item) return

  const fileName = getDownloadFileName(item)

  try {
    const response = await fetch(item.url)

    if (!response.ok) throw new Error('Unable to download media')

    const blobUrl = URL.createObjectURL(await response.blob())
    triggerDownload(blobUrl, fileName)
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
  } catch {
    triggerDownload(item.url, fileName)
  }
}

const highlightMessage = (element: HTMLElement) => {
  element.classList.remove('highlight-message')
  void element.offsetWidth
  element.classList.add('highlight-message')
  setTimeout(() => element.classList.remove('highlight-message'), 2500)
}

const goToCurrentMessage = () => {
  const messageId = currentItem.value?.id

  if (!messageId) return

  close()

  window.setTimeout(() => {
    const element = document.getElementById(`message-${messageId}`)

    if (!element) return

    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    highlightMessage(element)
  }, 150)
}

const normalizeRotation = (degrees: number) => {
  return ((degrees % 360) + 360) % 360
}

const rotateLeft = () => {
  if (currentItem.value?.type !== 'image') return

  rotation.value -= 90
}

const rotateRight = () => {
  if (currentItem.value?.type !== 'image') return

  rotation.value += 90
}

const handleWheel = (event: WheelEvent) => {
  if (currentItem.value?.type !== 'image') return

  if (event.deltaY < 0) {
    zoomIn()
  } else {
    zoomOut()
  }
}

const close = () => {
  isVisible.value = false
}

watch(
  () => [props.visible, props.initialItemId, props.items.length] as const,
  ([visible]) => {
    if (!visible) return

    setCurrentIndexFromInitialItem()
    resetTransform()
    isCaptionExpanded.value = false
  },
  { immediate: true },
)

watch(
  () => [currentItem.value?.id, currentItem.value?.caption, isVisible.value] as const,
  async () => {
    isCaptionExpanded.value = false
    await nextTick()
    measureCaptionOverflow()
  },
  { immediate: true },
)

useEventListener(window, 'keydown', (event) => {
  if (!isVisible.value) return

  if (event.key === 'Escape') close()
  if (event.key === 'ArrowRight') showNext()
  if (event.key === 'ArrowLeft') showPrevious()
  if (event.key === '+' || event.key === '=') zoomIn()
  if (event.key === '-') zoomOut()
  if (event.key.toLowerCase() === 'q' && currentItem.value?.type === 'image') rotateLeft()
  if (event.key.toLowerCase() === 'r' && currentItem.value?.type === 'image') rotateRight()
})

useEventListener(window, 'resize', measureCaptionOverflow)
</script>

<style>
.media-viewer-dialog {
  max-height: 100vh;
}

.media-viewer-dialog .p-dialog-header {
  display: none;
}

.media-viewer-dialog .p-dialog-content {
  height: 100vh;
  padding: 0;
  overflow: hidden;
}

.media-viewer-content {
  height: 100%;
}

.media-viewer-action {
  display: inline-flex;
  width: 2.25rem;
  height: 2.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: white;
  background: rgb(255 255 255 / 0.12);
  transition:
    background 0.15s ease,
    transform 0.15s ease;
}

.media-viewer-action:hover,
.media-viewer-nav:hover {
  background: rgb(255 255 255 / 0.2);
}

.media-viewer-action:active,
.media-viewer-nav:active {
  transform: scale(0.95);
}

.media-viewer-nav {
  position: absolute;
  z-index: 1;
  top: 50%;
  display: inline-flex;
  width: 3rem;
  height: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: white;
  background: rgb(255 255 255 / 0.12);
  transform: translateY(-50%);
  transition:
    background 0.15s ease,
    transform 0.15s ease;
}

.media-viewer-nav:active {
  transform: translateY(-50%) scale(0.95);
}

.media-viewer-caption-collapsed {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
